import * as PIXI from "pixi.js";
import { Spine } from "pixi-spine";
import { symbolData } from "../../SymbolData";
import { AnimationSettings, Settings } from "../../settings";
import anime from "animejs";
import { ASSET_NAME } from "../../assets";
import { SOUND_NAME } from "../../sounds";

class ReelController {
  constructor(
    symbolsContainer,
    spriteSheet,
    symbolsSpineData,
    reels,
    reelIndex,
    symbolHeight,
    handleReelSpinComplete,
    handlePlaySound
  ) {
    // Initialize properties for the reel controller
    this.symbolsContainer = symbolsContainer; // Container for symbols
    this.reelIndex = reelIndex; // Index of the current reel
    this.spritesheet = spriteSheet; // Spritesheet for symbol textures
    this.symbolsSpineData = symbolsSpineData; // Spine data for animations
    this.symbolHeight = symbolHeight; // Height of each symbol
    this.setSpinComplete = handleReelSpinComplete; // Callback for spin completion
    this.handlePlaySound = handlePlaySound;
    this.isStopping = false; // Flag to indicate if the reel is stopping
    this.hasStopped = false; // Flag to indicate if the reel has stopped
    this.reels = reels; // Array of symbols in the reel
  }

  // Returns the current instance of the ReelController
  getReelController() {
    return this;
  }

  // Updates the symbols in the reel with new data
  updateReels(newReels) {
    this.reels = newReels;
  }

  // Initiates the spinning of the reel
  spin() {
    if (this.spinTween) {
      this.spinTween.pause(); // Pause any existing spin animation
      this.spinTween = null;
    }

    this.hasStopped = false;
    this.isStopping = false;

    this.clearWinningSymbols(); // Clear any winning symbols
    this.resetReelPosition(); // Reset the reel's position

    this.symbolsContainer.y = Settings.START_POSITION * this.symbolHeight;
    this.symbolsContainer.alpha = 0.7; // Set initial transparency

    this.normalizedSpinSpeed =
      AnimationSettings.ANIMATION_SPEED * (this.symbolHeight / 100);

    let lastTime = performance.now(); // Initialize the time tracker

    // Create a continuous animation loop for spinning
    this.spinTween = anime({
      targets: {},
      loop: true,
      update: () => {
        const currentTime = performance.now();
        let deltaTime = (currentTime - lastTime) / 1000; // Time in seconds
        lastTime = currentTime;

        // Cap deltaTime to handle very low frame rates
        if (deltaTime > 0.05) deltaTime = 0.05;

        // Pass deltaTime to updateSymbols for consistent speed
        this.updateSymbols(10 * this.symbolHeight, deltaTime);
      },
    });
  }

  // Resizes symbols based on new height
  resizeSymbol(newSymbolHeight) {
    this.symbolHeight = newSymbolHeight;
    this.symbolsContainer.y = -this.symbolHeight;

    this.symbolsContainer.children.forEach((symbol, index) => {
      symbol.y = index * this.symbolHeight;

      ["win", "winBox", "dim"].forEach((name) => {
        const childSymbol = symbol.children.find(
          (child) => child.name === name
        );
        if (childSymbol) {
          const nativeBounds = childSymbol.getLocalBounds();
          const scaleFactor = Math.min(
            newSymbolHeight / (nativeBounds.height || 1),
            newSymbolHeight / (nativeBounds.width || 1)
          );

          let adjustment = 1;
          if (name === "win") {
            const symbolId = symbol.symbolId;
            adjustment = symbolData[symbolId]?.scalingAdjustment || 1;
          } else if (name === "winBox") {
            adjustment = 1.4;
          }

          childSymbol.scale.set(scaleFactor * adjustment);
        }
      });
    });
  }

  // Updates the position of symbols based on time delta
  updateSymbols(totalHeight, deltaTime) {
    this.symbolsContainer.children.forEach((symbol, index) => {
      // Move based on time delta to ensure consistent speed
      symbol.y += this.normalizedSpinSpeed * deltaTime;

      if (symbol.y >= totalHeight) {
        symbol.y -= totalHeight;

        this.updateSymbolTextureRandomly(symbol); // Update texture randomly
        this.moveLastSymbolOnTop(); // Move last symbol to the top
      }
    });
  }

  // Initiates the stopping of the reel
  stopSpin() {
    if (this.isStopping || this.hasStopped) {
      return;
    }
    this.startStopReelTween(); // Start the stop animation
  }

  // Handles the animation for stopping the reel
  startStopReelTween() {
    if (!this.spinTween || this.isStopping || this.hasStopped) return;

    this.spinTween.pause();
    this.spinTween = null; // Ensure no residual updates occur
    this.isStopping = true;

    for (let i = 0; i < 5; i++) {
      this.updateSymbolTexture(this.symbolsContainer.children[i], i);
    }

    let finalPosition =
      5 * this.symbolHeight -
      (this.symbolsContainer.children[0].y % this.symbolHeight);

    this.symbolsContainer.alpha = 1;

    // Animate the reel to its final stopping position
    anime({
      targets: this.symbolsContainer,
      y: `+=${finalPosition}`,
      duration:
        (finalPosition /
          (this.symbolHeight / AnimationSettings.STOP_DURATION)) *
        500,
      easing: function () {
        return function (t, overshoot = 1.2) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      begin: () => {
        this.handlePlaySound(SOUND_NAME.reelStop);
      },
      complete: () => {
        this.isStopping = false;
        this.hasStopped = true;
        if (this.reelIndex === 4) {
          this.setSpinComplete(true); // Mark spin as complete
        }
      },
    });
  }

  // Matches the position of winning symbols
  matchWinningSymbolPositions(index = 1) {
    this.animateWinningSymbol(this.symbolsContainer.children[index], index);
  }

  // Animates a winning symbol using spine animation
  animateWinningSymbol(symbol) {
    const symbolId = symbol.currentSymbolId;
    if (!symbolData[symbolId]) {
      return;
    }

    // Check if the symbol is already animated.
    const animatedSymbolIndex = this.findChildIndex(symbol.children, "win");
    if (animatedSymbolIndex) {
      this.showWinningSymbol(symbol);
      return; // Already animated, so exit
    }

    const spineData = this.symbolsSpineData[symbolData[symbolId].name];
    if (!spineData) {
      return;
    }

    const spineInstance = new Spine(spineData);
    spineInstance.position.set(0, 0);
    spineInstance.state.setAnimation(0, spineData.animations[0]?.name, true);
    spineInstance.zIndex = 10;

    const nativeBounds = spineInstance.getLocalBounds();
    const scaleFactor = Math.min(
      symbol.height / (nativeBounds.height || 1),
      symbol.height / (nativeBounds.width || 1)
    );

    if (spineInstance.scale) {
      spineInstance.scale.set(
        scaleFactor * symbolData[symbolId]?.scalingAdjustment
      );
    }

    spineInstance.name = "win";
    symbol.addChildAt(spineInstance, 2);
    symbol.children[0].visible = false; // Hide texture
  }

  // Animates the win box around a symbol
  animateWinBox(symbol) {
    // Check if the symbol is already animated.
    const winBoxIndex = this.findChildIndex(symbol.children, "winBox");
    if (winBoxIndex) {
      symbol.children[winBoxIndex].visible = true;
      return; // Already animated, so exit
    }
    const winboxSpine = PIXI.Assets.get(ASSET_NAME.winBox);
    const spineInstance = new Spine(winboxSpine.spineData);
    spineInstance.position.set(0, 0);
    spineInstance.state.setAnimation(0, "Win_Box_02", true);
    spineInstance.zIndex = 2;

    const nativeBounds = spineInstance.getLocalBounds();
    const scaleFactor = Math.min(
      this.symbolHeight / (nativeBounds.height || 1),
      this.symbolHeight / (nativeBounds.width || 1)
    );

    if (spineInstance.scale) {
      spineInstance.scale.set(scaleFactor * 1.4);
    }

    spineInstance.name = "winBox";
    symbol.addChild(spineInstance);
  }

  // Find the index of the child with the given name.
  findChildIndex(children, childName) {
    const index = children.findIndex((child) => child.name === childName);
    return index !== -1 ? index : false;
  }

  // Show winning symbol animation. Hide texture.
  showWinningSymbol(symbol) {
    const animatedSymbolIndex = this.findChildIndex(symbol.children, "win");
    if (animatedSymbolIndex) {
      symbol.children[0].visible = false;
      // Restart the animation.
      const spineInstance = symbol.children[animatedSymbolIndex];
      spineInstance.state.setAnimation(
        0,
        spineInstance.spineData.animations[0]?.name,
        true
      );
      spineInstance.visible = true;
    }
  }

  // Hide winning symbol animation. Show texture.
  hideWinningSymbol(symbol) {
    const animatedSymbolIndex = this.findChildIndex(symbol.children, "win");
    if (animatedSymbolIndex) {
      symbol.children[0].visible = true;
      symbol.children[animatedSymbolIndex].visible = false;
    }
  }

  // Show animations for all winning symbols
  showWinningSymbols() {
    [1, 2, 3].forEach((index) => {
      const symbol = this.symbolsContainer.children[index];
      if (symbol) {
        this.showWinningSymbol(symbol);
      }
    });
  }

  // Hide animations for all winning symbols
  hideWinningSymbols() {
    [1, 2, 3].forEach((index) => {
      const symbol = this.symbolsContainer.children[index];
      if (symbol) {
        this.hideWinningSymbol(symbol);
      }
    });
  }

  // Clear all winning symbols from the reel
  clearWinningSymbols() {
    this.removeSpineAnimations("win");
  }

  // Dim a specific symbol by overlaying a semi-transparent graphic
  dimSymbol(symbol) {
    const dimSymbolIndex = this.findChildIndex(symbol.children, "dim");
    // Create new dim symbol if it doesn't exist.
    if (!dimSymbolIndex) {
      const graphics = new PIXI.Graphics();
      graphics.beginFill(0x000000, 0.5); // Black color with 50% opacity
      graphics.drawRect(
        -this.symbolHeight / 2,
        -this.symbolHeight / 2,
        this.symbolHeight,
        this.symbolHeight
      );
      graphics.endFill();
      graphics.name = "dim";
      symbol.addChild(graphics);
    }
    // Otherwise, show the existing dim symbol.
    else {
      symbol.children[dimSymbolIndex].visible = true;
    }
  }

  // Create dim symbols spine for all symbols.
  dimAllSymbols() {
    [1, 2, 3].forEach((index) => {
      const symbol = this.symbolsContainer.children[index];
      this.dimSymbol(symbol);
    });
  }

  // Remove spine animations with a specific name from all symbols
  removeSpineAnimations(name) {
    this.symbolsContainer.children.forEach((symbol) => {
      const spineIndex = this.findChildIndex(symbol.children, name);
      if (spineIndex) {
        symbol.removeChildAt(spineIndex);
        symbol.children[0].visible = true;
      }
    });
  }

  // Hide elements of symbols with a specific name
  hideSymbolElements(name) {
    this.symbolsContainer.children.forEach((symbol) => {
      const symbolIndex = this.findChildIndex(symbol.children, name);
      if (symbolIndex) {
        symbol.children[symbolIndex].visible = false;
      }
    });
  }

  // Toggle visibility of a specific element within a symbol
  toggleSymbolVisibility(symbol, name) {
    const symbolIndex = this.findChildIndex(symbol.children, name);
    if (symbolIndex) {
      symbol.children[symbolIndex].visible =
        !symbol.children[symbolIndex].visible;
    }
  }

  // Reset the position of the reel to its starting position
  resetReelPosition() {
    this.symbolsContainer.y = Settings.START_POSITION * this.symbolHeight;
    for (let i = 5; i < this.symbolsContainer.children.length; i++) {
      this.moveLastSymbolOnTop();
    }
    for (let i = 0; i < this.symbolsContainer.children.length; i++) {
      this.symbolsContainer.children[i].y = i * this.symbolHeight;
    }
  }

  // Update the texture of a symbol based on its index in the reel
  updateSymbolTexture(symbol, index) {
    if (index < 0 || index >= this.reels.length) {
      return;
    }

    const symbolId = this.reels[index];

    if (symbolId === undefined || !symbolData[symbolId]) {
      return;
    }

    if (!this.spritesheet.textures) {
      return;
    }

    symbol.children[0].texture =
      this.spritesheet.textures[`${symbolData[symbolId].name}.png`];
    symbol.symbolId = symbolId; // Ensure this is set
    symbol.currentSymbolId = symbolId; // Keep a current reference for debugging
  }

  // Randomly update the texture of a symbol
  updateSymbolTextureRandomly(symbol) {
    const randomSymbolId = Math.floor(
      Math.random() * Settings.REELS[this.reelIndex].length
    );
    symbol.children[0].texture =
      this.spritesheet.textures[`${symbolData[randomSymbolId].name}.png`];
  }

  // Move the last symbol in the container to the top
  moveLastSymbolOnTop() {
    const lastSymbol = this.symbolsContainer.children.pop();
    this.symbolsContainer.children.unshift(lastSymbol);
  }
}

export default ReelController;
